/**
 * The head of one table's review: its name, where it stands with the case, its size, what
 * the file is about, and — behind an info button — how it was read.
 */

import { Info, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CardHeader, CardTitle } from '@/components/ui/card'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type {
  OutputSummary,
  ProjectionResult,
  Proposal,
  StructuredIngestTable,
} from '@/services/api/structuredIngest'
import { cn } from '@/utils/cn'
import { formatDate, formatDateTimeLocalized } from '@/utils/date'
import { Fold, HelpTip } from './recipe-cards'
import { sumValues } from './review-state'
import { POPOVER_SCROLLS } from './SchemaBand'

/** Run ids are "YYYYMMDDTHHMMSS", optionally followed by a uniqueness suffix. */
function runDate(run: string): string | null {
  const m = run.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/)
  if (!m) return null
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6])).toISOString()
}

function Receipt({ written }: { written: ProjectionResult }) {
  const { t, i18n } = useTranslation()
  const added = runDate(written.run)
  const parts = [
    added &&
      t('tableMapping.table.receipt.added', {
        date: formatDateTimeLocalized(added, i18n.language),
      }),
    t('tableMapping.count.entities', { count: sumValues(written.nodes) }),
    t('tableMapping.count.relationships', { count: sumValues(written.edges) }),
    written.prunedEdges > 0 &&
      t('tableMapping.table.receipt.replaced', { count: written.prunedEdges }),
    written.windows?.enabled && t('tableMapping.count.snippets', { count: written.windows.count }),
    written.warnings.length > 0 &&
      t('tableMapping.table.receipt.skipped', { count: written.warnings.length }),
    written.leftOut &&
      written.leftOut.rows > 0 &&
      t('tableMapping.leftOut.receipt', { count: written.leftOut.rows }),
  ].filter(Boolean)
  return <>{parts.join(', ')}</>
}

function StatusBadge({
  written,
  writtenAt,
  totals,
  changed,
}: {
  written: ProjectionResult | null
  writtenAt: string | null
  /** What the mapping would write now, to say what an update changes. */
  totals: OutputSummary['totals']
  changed: boolean
}) {
  const { t, i18n } = useTranslation()
  if (!written) return <Badge variant="muted">{t('tableMapping.table.notInCase')}</Badge>
  if (changed) {
    return (
      <Badge variant="info" className="gap-1">
        {t('tableMapping.table.changed')}
        <HelpTip>
          {t('tableMapping.table.delta', {
            entitiesBefore: sumValues(written.nodes),
            entitiesNow: totals.nodes ?? '?',
            relationshipsBefore: sumValues(written.edges),
            relationshipsNow: totals.edges ?? '?',
          })}
        </HelpTip>
      </Badge>
    )
  }
  return (
    <Badge variant="success" className="gap-1">
      {writtenAt
        ? t('tableMapping.table.inCaseSince', { date: formatDate(writtenAt, i18n.language) })
        : t('tableMapping.table.inCase')}
      <HelpTip>
        <Receipt written={written} />
      </HelpTip>
    </Badge>
  )
}

/** Only a file that did not go the plain table way says how it was read. */
function LaneNote({ lane }: { lane: string }) {
  const { t } = useTranslation()
  if (lane !== 'document_allowed' && lane !== 'document_fallback') return null
  return (
    <p
      className={cn(
        'text-xs',
        lane === 'document_fallback' ? 'text-warning-foreground' : 'text-muted-foreground'
      )}
    >
      {t(`tableMapping.table.lane.${lane}`)}
    </p>
  )
}

export function TableHeader({
  data: { table, recipe, caseContext, written, totals, changedSinceWrite, pending },
  onOpenRows,
}: {
  data: {
    table: StructuredIngestTable
    recipe: Proposal
    caseContext: string | null
    written: ProjectionResult | null
    totals: OutputSummary['totals']
    changedSinceWrite: boolean
    pending: boolean
  }
  onOpenRows: () => void
}) {
  const { t } = useTranslation()
  const facts = table.facts
  const subject = recipe.subject?.value
  return (
    <CardHeader className="gap-1.5 px-4 md:px-6">
      <CardTitle className="flex flex-wrap items-center gap-2 text-lg max-md:min-w-0">
        <span className="truncate max-md:whitespace-normal max-md:break-words" title={facts.name}>
          {facts.name}
        </span>
        <StatusBadge
          written={written}
          writtenAt={table.confirmed?.at ?? null}
          totals={totals}
          changed={changedSinceWrite}
        />
        {pending && (
          <Badge variant="outline" className="gap-1">
            <Loader2 aria-hidden className="size-3 animate-spin motion-reduce:animate-none" />
            {t('tableMapping.table.preparing')}
          </Badge>
        )}
        <span className="ml-auto flex items-center gap-1">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-9 md:h-8"
            onClick={onOpenRows}
          >
            {t('tableMapping.table.openRows')}
          </Button>
          <HowRead table={table} caseContext={caseContext} />
        </span>
      </CardTitle>
      <p className="text-sm text-muted-foreground">
        {t('tableMapping.table.size', {
          rows: t('tableMapping.count.rows', { count: facts.rows }),
          columns: t('tableMapping.count.columns', { count: facts.columns.length }),
        })}
        {subject && (
          <>
            {' '}
            {t('tableMapping.table.about', { subject })}
            {recipe.subject?.description && ` ${recipe.subject.description}`}
          </>
        )}
      </p>
      <LaneNote lane={table.lane.lane} />
    </CardHeader>
  )
}

function HowRead({
  table,
  caseContext,
}: {
  table: StructuredIngestTable
  caseContext: string | null
}) {
  const { t } = useTranslation()
  const facts = table.facts
  const encoding = (facts as { encoding?: string }).encoding ?? '—'
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          className="max-md:size-9"
          aria-label={t('tableMapping.howRead.title')}
        >
          <Info aria-hidden className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className={cn('w-96 text-xs', POPOVER_SCROLLS)}>
        <div className="flex flex-col gap-2">
          <p className="font-medium">{t('tableMapping.howRead.title')}</p>
          <ul className="flex flex-col gap-1 text-muted-foreground">
            <li>
              {t('tableMapping.howRead.table', {
                name: facts.name,
                rows: t('tableMapping.count.rows', { count: facts.rows }),
                columns: t('tableMapping.count.columns', { count: facts.columns.length }),
              })}
            </li>
            <li>
              {t('tableMapping.howRead.format', {
                delimiter: facts.delimiter ?? t('tableMapping.howRead.spreadsheet'),
                decimal: facts.decimal,
                encoding,
              })}
            </li>
            <li>
              {t('tableMapping.howRead.residue', {
                above: facts.residue_above_count,
                below: facts.residue_below_count,
              })}
            </li>
            <li>{table.lane.reason}</li>
            {table.parquetKey && (
              <li className="break-all">
                {t('tableMapping.howRead.evidence', { key: table.parquetKey })}
              </li>
            )}
            <li>
              {t('tableMapping.howRead.assistant', {
                model: table.llm.model ?? '—',
                seconds: table.llm.latency_s ?? '?',
                prompt: table.llm.usage?.prompt_tokens ?? '?',
                completion: table.llm.usage?.completion_tokens ?? '?',
              })}
            </li>
          </ul>
          {caseContext && (
            <Fold title={t('tableMapping.howRead.caseContext')}>
              <pre className="overflow-auto whitespace-pre-wrap rounded bg-muted p-2 md:max-h-40">
                {caseContext}
              </pre>
            </Fold>
          )}
          <Fold title={t('tableMapping.howRead.prompt')}>
            <pre className="overflow-auto whitespace-pre-wrap rounded bg-muted p-2 md:max-h-64">
              {table.prompt}
            </pre>
          </Fold>
        </div>
      </PopoverContent>
    </Popover>
  )
}
